
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { AnswerChoices } from "../create-poll.type";
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { debounceTime, distinctUntilChanged, tap } from "rxjs";

@Component({
    selector: 'answer-choices',
    templateUrl: 'answer-choices.component.html',
    styleUrls: ['.././create-poll-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule
]
})
export class AnswerChoicesComponent implements OnChanges {

    positiveOptionCtrl = new FormControl<string>('', [Validators.required]);
    negativeOptionCtrl = new FormControl<string>('', [Validators.required]);
    dynamicForm: FormGroup; // Main form group, will store dynamic fields

    get fields(): FormArray {
        return this.dynamicForm.get("fields") as FormArray;
    }

    @Input() options: AnswerChoices; 
    @Output() onOptionChange = new EventEmitter();

    @ViewChildren('nodeInput') nodeInputs: QueryList<ElementRef>;
    
    constructor(
        private fb: FormBuilder,
    ) {
        this.dynamicForm = this.fb.group({
            fields: this.fb.array([]),
        });

        this.positiveOptionCtrl.valueChanges.pipe(
            debounceTime(500),
            // distinctUntilChanged(),
            tap(() => {
                if(this.options){
                    this.options.choices[this.options.choices.length-1].label = this.positiveOptionCtrl.getRawValue();
                    const changeInfos = {
                        options: this.options,
                        optionsValidity: this.positiveOptionCtrl.valid && this.negativeOptionCtrl.valid
                    }
                    this.onOptionChange.emit(changeInfos)
                }  
            }),
        ).subscribe();
        this.negativeOptionCtrl.valueChanges.pipe(
            debounceTime(500),
            // distinctUntilChanged(),
            tap(() => {
                if(this.options){
                    this.options.choices[0].label = this.negativeOptionCtrl.getRawValue();
                    const changeInfos = {
                        options: this.options,
                        optionsValidity: this.positiveOptionCtrl.valid && this.negativeOptionCtrl.valid
                    }
                    this.onOptionChange.emit(changeInfos)
                }
            }),
        ).subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.options.currentValue && changes.options.currentValue.type === 'ScaleQuestion'){
            // keep the 2 extrems of the array, first is negative and last is positive option
            this.options.choices.splice(1, this.options.choices.length-2)
            const positive = this.options.choices[this.options.choices.length-1] ? this.options.choices[this.options.choices.length-1].label : '';
            const negative = this.options.choices[0] ? this.options.choices[0].label : '';
            this.positiveOptionCtrl.setValue(positive, {emitEvent: true});
            this.negativeOptionCtrl.setValue(negative, {emitEvent: true});
        }
        if(changes.options.currentValue?.choices && changes.options.currentValue.type === 'ClosedQuestion'){
            this.buildForm(changes.options.currentValue.choices); // Build the form based on the option choices 
        }
    }

    /**
     * Adds a new field to the dynamic form.
     */
    addField() {
        const fieldGroup = this.fb.group({
            value: new FormControl('', Validators.required),
            // value: "", // Value of the field
        });
        fieldGroup.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(() => this.onValueChanged());
        this.fields.push(fieldGroup);
        this.focusNewField();
    }

    focusNewField(){
        setTimeout(()=>{
            const lastInput: HTMLInputElement = this.nodeInputs.last.nativeElement.children[0].children[0];
            lastInput.focus();
        })
    }
    /**
     * Removes a field from the dynamic form at a specific index.
     * @param index Index of the field to be removed.
     */
    removeField(index: number) {
        this.fields.removeAt(index);
        this.onValueChanged()
    }

  /**
   * Dynamically creates the form controls based on the input data.
   */
    buildForm(data: {label: string}[]) {
        this.dynamicForm = this.fb.group({
            fields: this.fb.array([]), // re-initialize the array
        });
        switch(this.options.type){
            case 'ClosedQuestion':
                data.forEach((field) => {
                    const fieldGroup = this.fb.group({
                        value: new FormControl(field.label, Validators.required),
                        label: '', 
                        type: 'text',
                        required: true
                    });
                    fieldGroup.valueChanges.pipe(
                        debounceTime(500),
                        distinctUntilChanged()
                    ).subscribe(() => this.onValueChanged());
                    this.fields.push(fieldGroup);
                })
                break;
        }      
    }

    onValueChanged(): void {
        const myChangedGroup = this.fields.getRawValue();
        this.options.choices = myChangedGroup.map(group => {
            return { label: group.value }
        })
        const changeInfos = {
            options: this.options,
            optionsValidity: this.dynamicForm.valid
        }
        this.onOptionChange.emit(changeInfos)
    }
}
